import Check from "@uui/Check";
import ChevronDown from "@uui/ChevronDown";
import { compact, uniqBy } from "lodash/fp";
import { shallow } from "zustand/shallow";

import Button from "@mui/material/Button";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import usePulseStore from "@app/analytics/Pulse/stores/pulse";
import { EnterpriseWorkspaceTeam } from "@app/common/workspaces/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import MenuItem from "@rui/Dropdown/MenuItem";
import Menu from "@rui/Menu";
import { Row } from "@rui/Stack";

const MenuButton = styled(Button)(({ theme }) => ({
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  height: "48px",
  color: theme.palette.shades.neutral[600],
  padding: "4px 10px",
  minWidth: "200px",
  "&:hover": {
    borderColor: theme.palette.shades.primary[500],
  },
  "&.Mui-disabled": {
    color: theme.palette.shades.neutral[500],
    backgroundColor: theme.palette.shades.neutral[50],
  },
}));

const TeamSelector = ({
  loading,
  teams,
}: {
  loading: boolean;
  teams: EnterpriseWorkspaceTeam[];
}) => {
  const { anchorElement, onClose, onClick, open } = useAnchorElement();

  const [selectedTeam, setSelectedTeam] = usePulseStore(
    (state) => [state.forYourTeams.selectedTeam, state.setSelectedTeam],
    shallow
  );

  // ensure the selected teams is included in the dropdown even if it's not an option
  // for this particular date range
  const options = uniqBy("id", compact([...teams, selectedTeam]));

  const handleSelectTeam = (team: EnterpriseWorkspaceTeam) => {
    setSelectedTeam(team);
    onClose();
  };

  return (
    <>
      <MenuButton
        endIcon={<ChevronDown />}
        onClick={onClick}
        disabled={!selectedTeam}
      >
        <Row spacing={0.5} width="100%">
          <Typography variant="bodySecondaryMed">Team:</Typography>
          {loading ? (
            <Typography variant="bodySecondaryMed">-</Typography>
          ) : selectedTeam ? (
            <Typography variant="bodySecondaryMed">
              {selectedTeam.name}
            </Typography>
          ) : (
            <Typography variant="bodySecondaryMed">None available</Typography>
          )}
        </Row>
      </MenuButton>
      <Menu anchorEl={anchorElement} open={open} onClose={onClose}>
        {options.map((team) => (
          <MenuItem
            key={team.id}
            sx={{ m: 0, minWidth: "175px" }}
            onClick={() => handleSelectTeam(team)}
          >
            <Row width="100%" variant="apart">
              <Typography variant="bodySecondaryReg">{team.name}</Typography>
              {team.id === selectedTeam?.id && (
                <Check sx={{ fontSize: "18px" }} />
              )}
            </Row>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TeamSelector;
